/**
 * An on-screen keyboard: the totem has no physical one. It edits a value it does not own — the caller keeps the text
 * and gets every change. The old kiosk's (backend/frontend/src/components/touch/top-bar/KioskKeyboard.tsx,
 * docs/NEW-UI/07 D-07): the layout follows what is typed, capitals on at the start of a name or a sentence and off
 * after the first letter, the backspace repeating while held. No key takes the focus, so the field keeps its caret.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import css from './Keyboard.module.css';

/** `text` a name, `email` an address, `tel` a number, `message` a sentence for the assistant. */
export type KeyboardLayout = 'text' | 'email' | 'tel' | 'message';

const SHIFT = '⇧';
const BACK = '⌫';
const SPACE = ' ';

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const LETTERS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  [SHIFT, 'z', 'x', 'c', 'v', 'b', 'n', 'm', BACK],
];

const ROWS: Record<KeyboardLayout, string[][]> = {
  text: [...LETTERS, ["'", '-', SPACE]],
  email: [DIGITS, ...LETTERS, ['@', '.', '-', '_', SPACE]],
  tel: [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['+', '0', BACK]],
  message: [DIGITS, ...LETTERS, [',', '.', "'", '?', SPACE]],
};

/** Capitals at the start: a name and a sentence begin with one, an address and a number do not. */
const STARTS_CAPITAL: Record<KeyboardLayout, boolean> = { text: true, email: false, tel: false, message: true };

const REPEAT_AFTER_MS = 400;
const REPEAT_EVERY_MS = 100;

export function Keyboard({ value, onChange, onEnter, layout = 'text', spaceLabel, doneLabel, maxLength }: {
  value: string;
  onChange: (next: string) => void;
  onEnter?: () => void;
  layout?: KeyboardLayout;
  spaceLabel: string;
  doneLabel: string;
  maxLength?: number;
}) {
  const [caps, setCaps] = useState(STARTS_CAPITAL[layout] && value === '');
  const [layoutShown, setLayoutShown] = useState(layout);
  if (layoutShown !== layout) {
    setLayoutShown(layout);
    setCaps(STARTS_CAPITAL[layout] && value === '');
  }
  // the value as typed so far: a key or a repeat that comes before the next render builds on it, not on the stale prop
  const latest = useRef(value);
  useEffect(() => { latest.current = value; }, [value]);
  const emit = (next: string) => {
    latest.current = next;
    onChange(next);
  };
  const repeat = useRef<{ wait?: ReturnType<typeof setTimeout>; every?: ReturnType<typeof setInterval> }>({});
  const stopRepeat = () => {
    clearTimeout(repeat.current.wait);
    clearInterval(repeat.current.every);
    repeat.current = {};
  };
  useEffect(() => stopRepeat, []);

  const erase = () => emit(latest.current.slice(0, -1));
  const startErasing = () => {
    stopRepeat();
    erase();
    repeat.current.wait = setTimeout(() => { repeat.current.every = setInterval(erase, REPEAT_EVERY_MS); }, REPEAT_AFTER_MS);
  };
  const type = (key: string) => {
    if (maxLength !== undefined && latest.current.length >= maxLength) return;
    const isLetter = /^[a-z]$/.test(key);
    emit(latest.current + (isLetter && caps ? key.toUpperCase() : key));
    if (isLetter && caps && STARTS_CAPITAL[layout]) setCaps(false);
  };

  const rows = ROWS[layout];
  return (
    <div className={`${css.keyboard} ${layout === 'tel' ? css.tel : ''}`} role="group" aria-label="keyboard" data-layout={layout}
      onPointerDown={(e) => e.preventDefault()}>
      {rows.map((row, i) => {
        const last = i === rows.length - 1;
        return (
          <div key={i} className={css.row}>
            {row.map((key) => {
              if (key === SHIFT) {
                return <button key={key} type="button" className={`${css.key} ${css.wide} ${caps ? css.on : ''}`} aria-pressed={caps}
                  aria-label="shift" onClick={() => setCaps((c) => !c)}>{SHIFT}</button>;
              }
              if (key === BACK) {
                return <button key={key} type="button" className={`${css.key} ${css.wide}`} aria-label="backspace"
                  onPointerDown={startErasing} onPointerUp={stopRepeat} onPointerLeave={stopRepeat} onPointerCancel={stopRepeat}>
                  <Icon name="delete" size={30} />
                </button>;
              }
              if (key === SPACE) {
                return <button key="space" type="button" className={`${css.key} ${css.space}`} onClick={() => type(SPACE)}>{spaceLabel}</button>;
              }
              return <button key={key} type="button" className={css.key} onClick={() => type(key)}>{caps && /^[a-z]$/.test(key) ? key.toUpperCase() : key}</button>;
            })}
            {last && onEnter && (
              <button type="button" className={`${css.key} ${css.wide} ${css.enter}`} onClick={onEnter}>{doneLabel}</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
